import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { Html5Qrcode } from "html5-qrcode";
import jsQR from "jsqr";

/**
 * iOS / iPadOS (incl. iPadOS reporting as Mac with touch) detection.
 * iOS WebKit either lacks BarcodeDetector or exposes a non-functional one that
 * passes feature-detection but never decodes — so we must NOT use it there and
 * instead run our own jsQR loop over the (working) camera frames.
 */
function isAppleWebKit() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  return iOS || iPadOS;
}

/**
 * Scanner — fast path for tiny (~16 mm) QR stickers.
 *
 * Two execution modes:
 *   FAST (default when `window.BarcodeDetector` exists, Chrome/Android):
 *     - Attach our own <video> element with getUserMedia.
 *     - Loop `BarcodeDetector.detect(video)` on requestAnimationFrame.
 *     - Whole-frame scan (no qrbox crop, no hidden-canvas blit). 3-5× faster.
 *   FALLBACK (Safari < 17, older WebView):
 *     - Use html5-qrcode with the same continuous-AF + macro tweaks as before.
 *
 * After a decode the scanner does NOT release the camera. It enters a short
 * cooldown (1.5 s) and then auto-resumes, so the operator can rescan without a
 * full camera teardown/remount. Parent code can also call:
 *   ref.resume()       — manually exit cooldown immediately (e.g. "Rescan" tap)
 *   ref.softRestart()  — recreate the detector instance, keep the stream
 *
 * Extras in both modes:
 *   - Torch (LED flashlight) toggle when the active track exposes it.
 *   - 1× / 2× / 3× / 5× native zoom (CSS fallback when unsupported).
 *   - Tap-to-focus.
 *   - Short haptic buzz + audible blip on success.
 *   - Watchdog: 6 s with no detections post-resume → soft restart automatically.
 *
 * Props:
 *   onDecoded(text)   — invoked with the decoded payload (camera keeps running)
 *   onError(msg)      — invoked on permission denial or scanner failure
 *   hint              — small caption rendered below the camera frame
 *   warmupMs          — ignore decodes for N ms after start/resume (default 250)
 *   cooldownMs        — pause detection for N ms after each decode (default 1500)
 *   ignoreText        — if decoded payload equals this exact string, keep scanning
 */
const Scanner = forwardRef(function Scanner(
  { onDecoded, onError, hint, warmupMs = 250, cooldownMs = 1500, ignoreText = null },
  ref,
) {
  const hostRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);          // offscreen canvas for the jsQR path
  const qrRef = useRef(null);              // fallback html5-qrcode instance
  const trackRef = useRef(null);
  const capsRef = useRef(null);
  const detectorRef = useRef(null);
  const decoderRef = useRef("jsqr");       // "barcodedetector" (Android) | "jsqr" (iOS / no native detector)
  const fastModeRef = useRef(false);       // true once our own getUserMedia video pipeline is running
  const rafRef = useRef(0);
  const frameTimerRef = useRef(null);      // setTimeout handle for throttled jsQR loop
  const scanCountRef = useRef(0);          // frames actually processed (diagnostics)
  const stoppedRef = useRef(false);        // true after the component is being torn down
  const pausedRef = useRef(false);         // true during post-decode cooldown
  const cooldownTimerRef = useRef(null);
  const watchdogRef = useRef(null);
  const watchdogLastDetectRef = useRef(0);
  const startTsRef = useRef(0);
  const ignoreTextRef = useRef(ignoreText);
  useEffect(() => { ignoreTextRef.current = ignoreText; }, [ignoreText]);
  // Always call the LATEST onDecoded — the long-lived detect loop would otherwise
  // capture a stale closure (e.g. the zone selected before the camera started).
  const onDecodedRef = useRef(onDecoded);
  useEffect(() => { onDecodedRef.current = onDecoded; }, [onDecoded]);

  const [starting, setStarting] = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [cssZoom, setCssZoom] = useState(1);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [fastMode, setFastMode] = useState(false);
  const [paused, setPaused] = useState(false);   // UI mirror of pausedRef
  const [diag, setDiag] = useState("");          // tiny on-screen diagnostic line
  const hostIdRef = useRef(`m-scan-${Math.random().toString(36).slice(2, 9)}`);

  const _feedback = () => {
    try { navigator.vibrate?.(40); } catch { /* ignore */ }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 1500;
      g.gain.value = 0.08;
      o.start();
      setTimeout(() => { o.stop(); ctx.close().catch(() => {}); }, 70);
    } catch { /* ignore */ }
  };

  const _clearTimers = () => {
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
    if (frameTimerRef.current) {
      clearTimeout(frameTimerRef.current);
      frameTimerRef.current = null;
    }
  };

  const stopAll = useCallback(() => {
    stoppedRef.current = true;
    _clearTimers();
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (qrRef.current) {
      try { qrRef.current.stop().catch(() => {}); } catch { /* ignore */ }
      qrRef.current = null;
    }
    const v = videoRef.current;
    const stream = v?.srcObject;
    try { stream?.getTracks?.().forEach((t) => t.stop()); } catch { /* ignore */ }
    if (v) { try { v.srcObject = null; } catch { /* ignore */ } }
    trackRef.current = null;
    capsRef.current = null;
  }, []);

  // ---------- Resume / soft restart ----------

  const resume = useCallback(() => {
    if (stoppedRef.current) return;
    _clearTimers();
    pausedRef.current = false;
    setPaused(false);
    // Reset warmup so we don't immediately re-decode the same sticker.
    startTsRef.current = Date.now();
    watchdogLastDetectRef.current = Date.now();
    // If the loop has exited (e.g. after a decode), kick it back on.
    if (fastModeRef.current && !rafRef.current) {
      _startDetectLoop();
    }
    // Restart the watchdog
    _startWatchdog();
  }, []);

  const softRestart = useCallback(() => {
    if (stoppedRef.current) return;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (frameTimerRef.current) {
      clearTimeout(frameTimerRef.current);
      frameTimerRef.current = null;
    }
    // Recreate the BarcodeDetector (Android) — it sometimes gets stuck. On the
    // jsQR path there is no detector instance to recreate; the loop just resumes.
    if (decoderRef.current === "barcodedetector") {
      try {
        if (window.BarcodeDetector) {
          detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
        }
      } catch { /* ignore */ }
    }
    resume();
  }, [resume]);

  const captureFrame = useCallback(() => {
    return new Promise((resolve) => {
      const v = videoRef.current;
      if (!v || !v.videoWidth) { resolve(null); return; }
      // Freeze detection while we grab the still so a QR in view doesn't also register.
      pausedRef.current = true;
      setPaused(true);
      const maxW = 1600;
      const scale = Math.min(1, maxW / v.videoWidth);
      const w = Math.round(v.videoWidth * scale);
      const h = Math.round(v.videoHeight * scale);
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(v, 0, 0, w, h);
      c.toBlob((blob) => {
        setTimeout(() => { pausedRef.current = false; setPaused(false); }, 400);
        resolve(blob);
      }, "image/jpeg", 0.72);
    });
  }, []);

  useImperativeHandle(ref, () => ({ resume, softRestart, captureFrame }), [resume, softRestart, captureFrame]);

  // ---------- Watchdog ----------

  const _startWatchdog = () => {
    if (!fastModeRef.current) return;
    if (watchdogRef.current) clearInterval(watchdogRef.current);
    watchdogLastDetectRef.current = Date.now();
    watchdogRef.current = setInterval(() => {
      if (stoppedRef.current) return;
      // Live diagnostic: decoder + capture resolution + frames processed. Lets us
      // confirm remotely whether the loop is running (count climbing) vs. a decode
      // problem (count climbs but never locks) vs. a stalled pipeline (count stuck).
      try {
        const s = trackRef.current?.getSettings ? trackRef.current.getSettings() : {};
        setDiag(
          `${decoderRef.current === "jsqr" ? "jsQR" : "native"} · ${s.width || "?"}×${s.height || "?"}` +
          ` · z${(s.zoom || zoom || 1)} · frames ${scanCountRef.current}`,
        );
      } catch { /* ignore */ }
      if (pausedRef.current) return;
      // If 6 s have passed without ANY detection attempt completing successfully
      // (we update watchdogLastDetectRef inside the loop), do a soft restart.
      if (Date.now() - watchdogLastDetectRef.current > 6000) {
        // eslint-disable-next-line no-console
        console.warn("[Scanner] watchdog: no detect resolutions in 6s — soft-restarting");
        softRestart();
      }
    }, 1500);
  };

  // ---------- FAST MODE ----------

  const _handleDecoded = useCallback((decodedText) => {
    if (stoppedRef.current) return false;
    if (pausedRef.current) return false;
    if (Date.now() - startTsRef.current < warmupMs) return false;
    if (ignoreTextRef.current && String(decodedText) === String(ignoreTextRef.current)) return false;

    _feedback();

    // Enter cooldown: stop processing detects for cooldownMs, then auto-resume.
    pausedRef.current = true;
    setPaused(true);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (frameTimerRef.current) {
      clearTimeout(frameTimerRef.current);
      frameTimerRef.current = null;
    }
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = setTimeout(() => {
      cooldownTimerRef.current = null;
      // Auto-resume so the operator can scan again without tapping anything
      if (!stoppedRef.current) {
        pausedRef.current = false;
        setPaused(false);
        startTsRef.current = Date.now();
        watchdogLastDetectRef.current = Date.now();
        if (fastModeRef.current && !rafRef.current) _startDetectLoop();
      }
    }, cooldownMs);

    onDecodedRef.current?.(decodedText);
    return true;
  }, [warmupMs, cooldownMs]);

  // Decode a single frame. Returns the raw decoded string or null.
  const _decodeFrame = async () => {
    const v = videoRef.current;
    if (!v || v.readyState < 2 || !v.videoWidth) return null;

    if (decoderRef.current === "barcodedetector") {
      const detector = detectorRef.current;
      if (!detector) return null;
      const codes = await detector.detect(v);
      if (codes && codes.length) {
        let best = codes[0];
        let bestArea = 0;
        for (const c of codes) {
          const b = c.boundingBox;
          const area = (b?.width || 0) * (b?.height || 0);
          if (area > bestArea) { bestArea = area; best = c; }
        }
        return best?.rawValue || null;
      }
      return null;
    }

    // jsQR path: decode from an offscreen canvas. Two passes per frame:
    //   1) a CENTER SQUARE cropped at NATIVE resolution — keeps full sensor
    //      detail on the small (centered) sticker the operator is aiming at.
    //   2) if that misses, the whole frame downscaled — catches an off-centre or
    //      farther-away (larger) code.
    let canvas = canvasRef.current;
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvasRef.current = canvas;
    }
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    const vw = v.videoWidth, vh = v.videoHeight;

    // Pass 1 — center square at native resolution (capped for perf).
    const side = Math.min(vw, vh);
    const sx = (vw - side) >> 1;
    const sy = (vh - side) >> 1;
    const MAX = 1600;
    const out = Math.min(side, MAX);
    if (canvas.width !== out) canvas.width = out;
    if (canvas.height !== out) canvas.height = out;
    ctx.drawImage(v, sx, sy, side, side, 0, 0, out, out);
    let img = ctx.getImageData(0, 0, out, out);
    let result = jsQR(img.data, out, out, { inversionAttempts: "attemptBoth" });
    if (result?.data) return result.data;

    // Pass 2 — whole frame, downscaled.
    const fw = vw > 1024 ? 1024 : vw;
    const fscale = fw / vw;
    const fh = Math.max(1, Math.round(vh * fscale));
    if (canvas.width !== fw) canvas.width = fw;
    if (canvas.height !== fh) canvas.height = fh;
    ctx.drawImage(v, 0, 0, fw, fh);
    img = ctx.getImageData(0, 0, fw, fh);
    result = jsQR(img.data, fw, fh, { inversionAttempts: "attemptBoth" });
    return result?.data || null;
  };

  const _startDetectLoop = () => {
    const v = videoRef.current;
    if (!v) return;
    const useRaf = decoderRef.current === "barcodedetector";

    const loop = async () => {
      if (stoppedRef.current || pausedRef.current) {
        rafRef.current = 0;
        return;
      }
      try {
        const value = await _decodeFrame();
        scanCountRef.current += 1;
        watchdogLastDetectRef.current = Date.now();
        if (value && !stoppedRef.current && !pausedRef.current) {
          const consumed = _handleDecoded(value);
          if (consumed) return; // cooldown engaged; loop will be restarted on resume
        }
      } catch {
        /* common: video not ready yet — ignore */
      }
      if (stoppedRef.current || pausedRef.current) {
        rafRef.current = 0;
        return;
      }
      if (useRaf) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        // Throttle the (heavier) jsQR canvas decode to ~15 fps to keep the UI smooth.
        frameTimerRef.current = setTimeout(() => {
          frameTimerRef.current = null;
          rafRef.current = requestAnimationFrame(loop);
        }, 66);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  const startFastMode = useCallback(async (decoderMode) => {
    decoderRef.current = decoderMode;
    fastModeRef.current = true;
    if (decoderMode === "barcodedetector") {
      detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
    } else {
      detectorRef.current = null;
    }
    const wantHiRes = decoderMode === "jsqr"; // iOS: more pixels for the JS decoder

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: "environment" },
        // iOS: capture as high as the camera allows so a tiny (20 mm) sticker
        // lands on enough real sensor pixels for jsQR (the center-crop keeps that
        // detail). Android (BarcodeDetector) stays at 720p for speed.
        width: wantHiRes ? { min: 1280, ideal: 2560, max: 3840 } : { ideal: 1280 },
        height: wantHiRes ? { min: 720, ideal: 1440, max: 2160 } : { ideal: 720 },
        frameRate: { ideal: 30, max: 30 },
        advanced: [{ focusMode: "continuous" }],
      },
    });

    const v = videoRef.current;
    if (!v) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    v.srcObject = stream;
    v.setAttribute("playsinline", "true");
    v.muted = true;

    // Wait for the first frame, then start the detect loop. Avoids the "video
    // not ready" spam that some browsers throw before HAVE_ENOUGH_DATA.
    await new Promise((resolve) => {
      const done = () => { v.removeEventListener("loadeddata", done); resolve(); };
      if (v.readyState >= 2) resolve();
      else v.addEventListener("loadeddata", done);
    });
    try { await v.play(); } catch (e) {
      // Surface play() failures — operators get a useful error instead of a frozen viewfinder
      setPermissionError("Camera preview could not start: " + (e?.message || String(e)));
      stream.getTracks().forEach((t) => t.stop());
      throw e;
    }

    const track = stream.getVideoTracks()[0];
    trackRef.current = track;
    const caps = track.getCapabilities ? track.getCapabilities() : {};
    capsRef.current = caps;

    try {
      const advanced = [];
      if (caps.focusMode && caps.focusMode.includes("continuous")) advanced.push({ focusMode: "continuous" });
      if (caps.focusDistance && typeof caps.focusDistance.min === "number") {
        advanced.push({ focusDistance: caps.focusDistance.min });
      }
      if (advanced.length) await track.applyConstraints({ advanced });
    } catch { /* non-fatal */ }

    if (caps.zoom && typeof caps.zoom.max === "number" && caps.zoom.max > 1) setZoomSupported(true);
    if (caps.torch === true) setTorchSupported(true);

    // iOS small-sticker boost: auto-apply ~2× native zoom so the QR fills more
    // of the frame for the JS decoder. Operator can still adjust via the buttons.
    if (wantHiRes) {
      try {
        if (caps.zoom && typeof caps.zoom.max === "number") {
          const zMin = caps.zoom.min || 1;
          const target = Math.min(2, caps.zoom.max);
          if (target > zMin) {
            await track.applyConstraints({ advanced: [{ zoom: target }] });
            setZoom(target);
            setCssZoom(1);
          }
        }
      } catch { /* non-fatal */ }
    }

    const settings = track.getSettings ? track.getSettings() : {};
    setDiag(`${decoderMode === "jsqr" ? "jsQR" : "native"} · ${settings.width || "?"}×${settings.height || "?"}`);

    startTsRef.current = Date.now();
    watchdogLastDetectRef.current = Date.now();
    _startDetectLoop();
    _startWatchdog();
    setStarting(false);
  }, []);

  // ---------- FALLBACK MODE (html5-qrcode) ----------

  const startFallbackMode = useCallback(async () => {
    if (!hostRef.current) return;
    const qr = new Html5Qrcode(hostIdRef.current, {
      verbose: false,
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    });
    qrRef.current = qr;

    await qr.start(
      { facingMode: "environment" },
      {
        fps: 20,
        qrbox: (vw, vh) => {
          const minEdge = Math.min(vw, vh);
          const edge = Math.floor(minEdge * 0.8);
          return { width: edge, height: edge };
        },
        aspectRatio: 1.0,
        disableFlip: false,
        // iOS (WebKit) has no BarcodeDetector, so this fallback runs the JS
        // (ZXing) decoder. Small ~16-20 mm stickers decode reliably only when
        // the frame carries enough pixels — request the highest resolution the
        // camera can deliver (capped so we don't blow up memory on huge sensors).
        videoConstraints: {
          facingMode: "environment",
          width: { min: 1280, ideal: 1920, max: 3840 },
          height: { min: 720, ideal: 1080, max: 2160 },
          frameRate: { ideal: 30, max: 30 },
          advanced: [{ focusMode: "continuous" }],
        },
      },
      (decodedText) => _handleDecoded(decodedText),
      () => { /* no-QR per frame */ },
    );

    startTsRef.current = Date.now();

    try {
      const videoEl = hostRef.current.querySelector("video");
      const stream = videoEl?.srcObject;
      const track = stream?.getVideoTracks?.()?.[0];
      if (track) {
        trackRef.current = track;
        videoRef.current = videoEl;
        const caps = track.getCapabilities ? track.getCapabilities() : {};
        capsRef.current = caps;

        try {
          const adv = [];
          if (caps.focusMode && caps.focusMode.includes("continuous")) adv.push({ focusMode: "continuous" });
          if (caps.focusDistance && typeof caps.focusDistance.min === "number") {
            adv.push({ focusDistance: caps.focusDistance.min });
          }
          if (adv.length) await track.applyConstraints({ advanced: adv });
        } catch { /* non-fatal */ }

        if (caps.zoom && typeof caps.zoom.max === "number" && caps.zoom.max > 1) setZoomSupported(true);
        if (caps.torch === true) setTorchSupported(true);

        // Small-sticker boost (iOS especially): the JS decoder needs the QR to
        // occupy a decent share of the frame. Auto-apply a moderate native zoom
        // so a ~20 mm sticker held ~10 cm away fills enough pixels to decode.
        // The operator can still change it via the 1×/2×/3×/5× buttons.
        try {
          if (caps.zoom && typeof caps.zoom.max === "number") {
            const zMin = caps.zoom.min || 1;
            const target = Math.min(2, caps.zoom.max);
            if (target > zMin) {
              await track.applyConstraints({ advanced: [{ zoom: target }] });
              setZoom(target);
              setCssZoom(1);
            }
          }
        } catch { /* non-fatal */ }
      }
    } catch { /* optional */ }

    setStarting(false);
  }, [_handleDecoded]);

  // ---------- Mount ----------

  useEffect(() => {
    stoppedRef.current = false;
    pausedRef.current = false;

    const apple = isAppleWebKit();
    // On Apple/WebKit, BarcodeDetector is either missing or non-functional
    // (passes feature detection but never decodes) — always use jsQR there.
    const canUseNativeDetector =
      !apple &&
      typeof window !== "undefined" &&
      typeof window.BarcodeDetector === "function";

    const start = async () => {
      try {
        let decoderMode = "jsqr";
        if (canUseNativeDetector) {
          try {
            const formats = await window.BarcodeDetector.getSupportedFormats();
            if (formats && formats.includes("qr_code")) decoderMode = "barcodedetector";
          } catch { /* keep jsqr */ }
        }
        // Both decoder modes use our own getUserMedia <video> pipeline.
        setFastMode(true);
        await startFastMode(decoderMode);
      } catch (err) {
        // Last resort only if our own camera pipeline failed to start at all.
        try {
          setFastMode(false);
          fastModeRef.current = false;
          await startFallbackMode();
        } catch (err2) {
          setPermissionError(err2?.message || err?.message || String(err));
          setStarting(false);
          onError?.(err2?.message || err?.message || "Camera access failed");
        }
      }
    };

    start();
    return () => { stopAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Zoom / Torch / Tap-to-focus ----------

  const applyZoom = async (level) => {
    setZoom(level);
    const track = trackRef.current;
    const caps = capsRef.current;
    if (track && caps && caps.zoom) {
      const target = Math.min(caps.zoom.max, Math.max(caps.zoom.min || 1, level));
      try {
        await track.applyConstraints({ advanced: [{ zoom: target }] });
        setCssZoom(1);
        return;
      } catch { /* fall through */ }
    }
    setCssZoom(level);
  };

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      /* ignored */
    }
  };

  const onVideoTap = async (e) => {
    const track = trackRef.current;
    const caps = capsRef.current;
    if (!track || !caps || !caps.pointsOfInterest) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    try {
      const adv = [{ pointsOfInterest: [{ x, y }] }];
      if (caps.focusMode && caps.focusMode.includes("single-shot")) adv.push({ focusMode: "single-shot" });
      await track.applyConstraints({ advanced: adv });
    } catch { /* non-fatal */ }
  };

  const zoomLevels = [1, 2, 3, 5];

  return (
    <div>
      <div style={{ position: "relative" }} onClick={onVideoTap}>
        {fastMode ? (
          <div
            className="m-scanner-host"
            data-testid="mobile-scanner-host"
            ref={hostRef}
            style={{
              transform: cssZoom > 1 ? `scale(${cssZoom})` : undefined,
              transformOrigin: "center",
              transition: "transform 120ms ease-out",
              overflow: "hidden",
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
        ) : (
          <div
            id={hostIdRef.current}
            ref={hostRef}
            className="m-scanner-host"
            data-testid="mobile-scanner-host"
            style={{
              transform: cssZoom > 1 ? `scale(${cssZoom})` : undefined,
              transformOrigin: "center",
              transition: "transform 120ms ease-out",
            }}
          />
        )}

        {!starting && !permissionError && (
          <>
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                gap: 4,
                padding: 4,
                background: "rgba(0,0,0,0.55)",
                borderRadius: 999,
                backdropFilter: "blur(6px)",
              }}
              data-testid="scanner-zoom-controls"
            >
              {zoomLevels.map((lvl) => (
                <button
                  key={lvl}
                  onClick={(e) => { e.stopPropagation(); applyZoom(lvl); }}
                  aria-label={`Zoom ${lvl}x`}
                  data-testid={`scanner-zoom-${lvl}x`}
                  style={{
                    minWidth: 30,
                    height: 30,
                    borderRadius: 999,
                    border: "none",
                    fontFamily: "Chivo, monospace",
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: "pointer",
                    color: zoom === lvl ? "#0c0a09" : "#e7e5e4",
                    background: zoom === lvl ? "#fdba74" : "rgba(255,255,255,0.08)",
                  }}
                >
                  {lvl}×
                </button>
              ))}
            </div>

            {torchSupported && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleTorch(); }}
                aria-label={torchOn ? "Turn torch off" : "Turn torch on"}
                data-testid="scanner-torch-toggle"
                style={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: "none",
                  cursor: "pointer",
                  background: torchOn ? "#fdba74" : "rgba(0,0,0,0.55)",
                  color: torchOn ? "#0c0a09" : "#e7e5e4",
                  fontSize: 18,
                  backdropFilter: "blur(6px)",
                }}
                title={torchOn ? "Torch on" : "Torch off"}
              >
                {torchOn ? "🔦" : "💡"}
              </button>
            )}

            {paused && (
              <div
                style={{
                  position: "absolute",
                  bottom: 10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: "rgba(0,0,0,0.55)",
                  backdropFilter: "blur(6px)",
                  color: "#fdba74",
                  fontFamily: "Chivo",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.4,
                }}
                data-testid="scanner-paused"
              >
                · scanned · resuming ·
              </div>
            )}
          </>
        )}
      </div>
      {hint && <div style={{ fontSize: 12, color: "#a8a29e", marginTop: 8, textAlign: "center" }}>{hint}</div>}
      {!starting && !permissionError && (
        <div style={{ fontSize: 11, color: "#78716c", marginTop: 6, textAlign: "center" }}>
          Tip: hold the camera ~10 cm away. Tap the viewfinder to refocus.
          {torchSupported && " Tap the bulb to turn the torch on for dark / transparent stickers."}
          {!zoomSupported && cssZoom > 1 && " (zoom is digital-only on this device)"}
        </div>
      )}
      {!starting && !permissionError && diag && (
        <div
          data-testid="scanner-diag"
          style={{ fontSize: 10, color: "#57534e", marginTop: 4, textAlign: "center", fontFamily: "monospace" }}
        >
          {diag}
        </div>
      )}
      {starting && (
        <div style={{ fontSize: 12, color: "#a8a29e", marginTop: 8, textAlign: "center" }}>Starting camera…</div>
      )}
      {permissionError && (
        <div className="m-error-banner">
          Camera unavailable: {permissionError}. Allow camera permission and reload, or enter the code manually.
        </div>
      )}
    </div>
  );
});

export default Scanner;
